"use client";

import { itemPageState } from "@/atoms/item/item-page-state";
import { pageNumberState } from "@/atoms/order/page-number-state";
import { DEBOUNCE_TIME } from "@/common/constant/debounce-time";
import { initPage } from "@/common/constant/init-page";
import { ItemAdminOrderTypeExpanded } from "@/common/enum/item-admin-order-type";
import { PaginationSize } from "@/common/enum/pagination-size";
import { ItemType } from "@/common/enum/item-type";
import { ItemMainBar } from "@/components/bar/ItemMainBar";
import ItemMainHeader from "@/components/header/ItemMainHeader";
import ItemDetailModal from "@/components/modal/ItemDetailModal";
import { SingleProductCreatePageModalPanel } from "@/components/panel/SingleProductPageModalPanel";
import { ItemMainTable } from "@/components/table/item-main/ItemMainTable";
import { PageWrapper } from "@/components/wrapper/PageWrapper";
import { itemService } from "@/services/item-service";
import { BasePageDto } from "@/types/dto/base-page-dto";
import { ItemDetailDto } from "@/types/dto/item-detail-dto";
import { ItemPageAdminPayload } from "@/types/item/payload/item-page-payload";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useRecoilState } from "recoil";

export default function SingleProductPage() {
  const [searchItemName, setSearchItemName] = useState<string>("");
  const [itemSortType, setItemSortType] = useState<ItemAdminOrderTypeExpanded>(
    ItemAdminOrderTypeExpanded.ALL
  );
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [itemPage, setItemPage] =
    useRecoilState<BasePageDto<ItemDetailDto>>(itemPageState);
  const [selectedItem, setSelectedItem] = useState<ItemDetailDto | undefined>();
  const [pageNumber, setPageNumber] = useRecoilState(pageNumberState);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchItemName(event.target.value);
  };

  const handleSortChange = (sortType: ItemAdminOrderTypeExpanded) => {
    setItemSortType(sortType);
  };

  const handleCategoryChange = (categoryIds: number[]) => {
    setCategoryIds(categoryIds);
  };

  const handlePageChange = (currentPage: number) => {
    setItemPage({ ...itemPage, currentPage });
  };

  const handleItemNameClick = async (itemId: number) => {
    console.log("handleItemNameClick called with itemId:", itemId);
    try {
      const response = await itemService.getById({ id: itemId });
      console.log("API response:", response);
      if (response.ok) {
        console.log("Setting selectedItem:", response.data);
        setSelectedItem(response.data);
      } else {
        console.log("API response not ok:", response);
      }
    } catch (error) {
      console.log("Error in handleItemNameClick:", error);
    }
  };

  const clearSelectedItem = () => {
    setSelectedItem(undefined);
  };

  const getPage = async (input: ItemPageAdminPayload) => {
    try {
      const response = await itemService.getPageAdmin(input);
      setItemPage(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  const getPageDebounce = useCallback(
    debounce(async (input) => {
      await getPage(input);
    }, DEBOUNCE_TIME),
    []
  );

  const defaultPayload: ItemPageAdminPayload = {
    page: pageNumber,
    size: PaginationSize.SIZE_12,
    orderType: itemSortType,
    itemType: ItemType.ITEM,
    name: searchItemName,
    categoryIds,
  };

  const refreshData = useCallback(() => {
    getPage(defaultPayload);
  }, [defaultPayload]);

  useEffect(() => {
    getPageDebounce(defaultPayload);
  }, [pageNumber]);

  useEffect(() => {
    getPageDebounce(defaultPayload);
    setPageNumber(1);
  }, [searchItemName, itemSortType, categoryIds]);

  useEffect(() => {
    return () => {
      setItemPage(initPage);
    };
  }, []);

  return (
    <PageWrapper>
      <div className="flex flex-col w-[1648px]">
        <ItemMainHeader />
        <ItemMainBar
          totalElements={itemPage.totalElements}
          searchItemName={searchItemName}
          handleSearchChange={handleSearchChange}
          handleCategoryChange={handleCategoryChange}
        />
        <ItemMainTable
          itemPage={itemPage}
          handleItemNameClick={handleItemNameClick}
          handlePageChange={handlePageChange}
          itemSortType={itemSortType}
          handleSortChange={handleSortChange}
          refreshData={refreshData}
        />
        {selectedItem && (
          <ItemDetailModal
            selectedItem={selectedItem}
            clearSelectedItem={clearSelectedItem}
          />
        )}
      </div>
      <SingleProductCreatePageModalPanel />
    </PageWrapper>
  );
}
